import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { FormattedMessage, injectIntl, intlShape } from 'react-intl'
import Dropdown from 'react-dropdown'
import { push as pushAction } from 'react-router-redux'

import { updateReservationAndQuery, updateReservationData } from 'actions/reservation'
import { fetch } from 'actions/events'

import ReservationDateRequest from 'components/ReservationDateRequest'
import CouponCodeInput from 'components/CouponCodeInput'

import { languageByCode } from 'utils'

function getExtras({ event, events, reservation }) {
  if (!event) {
    return {
      eventExtras: [],
      selectedExtras: {},
      guests: 0,
    }
  }

  const reservationEvent = events[event._id][reservation.data.language]
  const { extras: eventExtras = [] } = reservationEvent
  const {
    data: {
      extras: selectedExtras = {},
      guests,
    } = {},
  } = reservation

  return {
    eventExtras,
    selectedExtras,
    guests,
  }
}

class EventReservation extends React.Component {
  constructor(props) {
    super(props)

    this.state = { showModal: false }
  }

  componentDidMount() {
    const { event, reservation, updateReservation } = this.props
    const data = { timezone: '' }

    if (event.reservationMode === 'video' && !reservation.data.timezone) {
      data.timezone = window.Intl
        ? (new Intl.DateTimeFormat()).resolvedOptions().timeZone
        : ''
    }

    updateReservation({ data })
  }

  componentDidUpdate() {
    this.checkExtras(this.props)
  }

  onNoteChanged(e) {
    const { updateReservation } = this.props
    updateReservation({ data: { note: e.target.value } })
  }

  onDateChanged(date, type) {
    const { updateReservation } = this.props
    updateReservation({ data: { start: date }, type })
  }

  onGuestsChanged(guestsData) {
    const { event, fetchEvent, reservation, updateReservation } = this.props

    const { language, extras } = reservation.data
    const guests = Number(guestsData.value)

    fetchEvent(event._id, language, guests, false, extras)
      .then(() => updateReservation({ data: { language, guests, start: '' } }))
  }

  onLanguageChanged(languageData) {
    const { event, fetchEvent, reservation, updateReservation } = this.props

    const { guests, extras } = reservation.data
    const language = languageData.value

    fetchEvent(event._id, language, guests, false, extras)
      .then(() => updateReservation({ data: { language, guests, start: '' } }))
  }

  onTimeZoneChanged(timeZoneData) {
    this.props.updateReservation({ data: { timezone: timeZoneData.value } })
  }

  onExtraChanged(id) {
    return ({ value }) => {
      const { event, fetchEvent, reservation, updateReservation } = this.props

      const { language, guests, extras: oldExtras } = reservation.data
      const extras = { ...oldExtras, [id]: value }

      fetchEvent(event._id, language, guests, false, extras)
        .then(() => updateReservation({ data: { language, guests, extras, start: '' } }))
    }
  }

  getReservationFormInput(label, values, value, onChange, key, dropdownProps = {}) {
    return (
      <div className="reservation-form__field" key={key}>
        <label className="reservation-form__label">
          <FormattedMessage id={label} />
        </label>
        <div className="reservation-form__input">
          <Dropdown
            options={values}
            onChange={onChange}
            value={value}
            {...dropdownProps}
          />
        </div>
      </div>
    )
  }

  getReservationExtras({ event, events, reservation }) {
    const reservationExtras = []
    const {
      eventExtras,
      selectedExtras,
      guests,
    } = getExtras({ event, events, reservation })

    let key = 0
    for (const extra of eventExtras) {
      const { id, label, maxValue } = extra
      const optionsLength = maxValue === 0
        ? guests + 1
        : maxValue + 1
      const value = selectedExtras[id] || 0
      reservationExtras.push(
        this.getReservationFormInput(
          label,
          Array.from({ length: optionsLength }, (v, k) => k),
          value.toString(),
          this.onExtraChanged(id),
          key++
        )
      )
    }

    return reservationExtras
  }

  checkExtras({ event, events, reservation, updateReservation }) {
    const {
      eventExtras,
      selectedExtras,
      guests,
    } = getExtras({ event, events, reservation })

    const extras = {}
    for (const extra of eventExtras) {
      const { id, maxValue } = extra
      const optionsLength = maxValue === 0
        ? guests + 1
        : maxValue + 1
      const currentValue = selectedExtras[id]
      if (currentValue >= optionsLength) {
        extras[id] = optionsLength - 1
      }
    }
    if (Object.entries(extras).length) {
      updateReservation({ data: { extras } })
    }
  }

  handleSubmit(e) {
    e.preventDefault()
    const { event, updateReservation, push, locale } = this.props

    updateReservation({
      id: event._id,
      step: 'auth',
      data: { ui_language: locale, code: '' },
    })
    push(`${window.location.pathname}/reservation${window.location.search}`)
  }

  openModal(e) {
    e.preventDefault()
    this.setState({ showModal: true })
  }

  closeModal() {
    this.setState({ showModal: false })
  }

  render() {
    const { event, events, reservation, locale } = this.props
    const { formatMessage } = this.props.intl

    const reservationEvent = events[event._id][reservation.data.language]

    if (!reservationEvent) {
      return null
    }

    // NOTE: this calculation is handy but it might send length < 0 if event.places is 0
    let dropdownLength = event.places - event.minimumSelectablePlaces + 1
    dropdownLength = dropdownLength >= 0
      ? dropdownLength
      : 0
    // NOTE END
    const dropdownValues = Array(dropdownLength)
      .fill()
      .map((_, i) => i + event.minimumSelectablePlaces)

    let titleComponent = null
    let mainComponent = null
    if (event.reservationMode === 'view') {
      titleComponent = (
        <span>{ event.reservationTitle }</span>
      )
      mainComponent = (
        <div
          className="wrap pad--both-2"
          dangerouslySetInnerHTML={{
            __html: event.reservationMessage,
          }}
        >
        </div>
      )
    } else {
      let reservationComponent = null
      if (
        reservationEvent.propose ||
        reservationEvent.dates.length
      ) {
        reservationComponent = (
          <ReservationDateRequest
            selected={this.onDateChanged.bind(this)}
            event={reservationEvent}
            reservation={reservation}
            locale={locale}
          />
        )
      } else {
        reservationComponent = (
          <h2><FormattedMessage id="app.event.reservation.no-dates-available.message" /></h2>
        )
      }

      titleComponent = (
        <FormattedMessage id="app.event.reservation.title" />
      )
      mainComponent = (
        <form className="reservation-form" onSubmit={this.handleSubmit.bind(this)}>
          <div className="reservation-box__body reservation-form__fields">
            {
              this.getReservationFormInput(
                event.default_product_plural || 'app.event.reservation.people',
                dropdownValues,
                reservation.data.guests.toString(),
                this.onGuestsChanged.bind(this)
              )
            }
            { this.getReservationExtras({ event, events, reservation }) }
          </div>
          <div className="reservation-box__body reservation-form__fields">
            {
              this.getReservationFormInput(
                'app.event.reservation.language',
                reservationEvent.languages,
                languageByCode(reservation.data.language, reservationEvent.languages),
                this.onLanguageChanged.bind(this)
              )
            }
            <div className="reservation-form__field reservation-form__field--coupon">
              <label className="reservation-form__label" style={{ verticalAlign: 'middle' }}>
                <FormattedMessage id="app.event.reservation.coupon" />
              </label>
              <div className="reservation-form__input">
                <CouponCodeInput
                  width="180px"
                  highlightOnSuccess
                  className="border--none"
                />
              </div>
            </div>
          </div>
          <div className="reservation-box__body">
            {reservationComponent}
            <div className="form__field">
              <label className="reservation-form__label">
                <FormattedMessage id="app.event.reservation.notes" />
              </label>
              <textarea
                className="form__input"
                value={reservation.data.note}
                onChange={this.onNoteChanged.bind(this)}
              />
            </div>
            <hr id="event-reservation__hr" />
            <div className="reservation-form__actions">
              <button
                className="button--primary button--large button--expand"
                type="submit"
                id="button--init-checkout"
                disabled={!reservation.data.start}
              >
                {formatMessage({ id: 'app.event.cta.book' })}
              </button>
            </div>
          </div>
        </form>
      )
    }

    return (
      <div className="wrap">
        <div id="reservation" className="reservation-box">
          <h2 className="reservation-box__title">
            { titleComponent }
          </h2>
          { mainComponent }
        </div>
      </div>
    )
  }
}

EventReservation.propTypes = {
  event: PropTypes.object.isRequired,
  events: PropTypes.object.isRequired,
  fetchEvent: PropTypes.func.isRequired,
  reservation: PropTypes.object.isRequired,
  updateReservation: PropTypes.func.isRequired,
  updateReservationDataAction: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  push: PropTypes.func.isRequired,
}

function mapStateToProps(state) {
  return {
    events: state.events,
    reservation: state.reservation,
    locale: state.ui.locale,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchEvent: fetch,
    updateReservation: updateReservationAndQuery,
    updateReservationDataAction: updateReservationData,
    push: pushAction,
  }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(EventReservation))
